import { useState, useEffect, useMemo, FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { ErrorBanner } from "../components/ErrorBanner";
import { FilesField } from "../components/FilesField";
import { getPost, updatePost, PostUpdateIn, PostFileIn } from "../api/posts";
import { ExternalUrlKind } from "../types/gordost";

function detectUrlKind(url: string): ExternalUrlKind {
  if (url.includes("rutube.ru")) return "rutube";
  if (url.includes("vk.com") || url.includes("vkvideo.ru")) return "vk";
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  return "other";
}

export function PostEditPage() {
  const { id } = useParams<{ id: string }>();
  const postId = parseInt(id || "0", 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: post, isLoading, error: queryError } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => getPost(postId),
    enabled: postId > 0,
  });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [externalUrl, setExternalUrl] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<PostFileIn[]>([]);
  const [filesChanged, setFilesChanged] = useState(false);

  // s3_key → presigned URL уже загруженных картинок (для превью-миниатюр).
  const existingPreviews = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of post?.files ?? []) {
      if (f.mime_type.startsWith("image/") && f.download_url) {
        map[f.s3_key] = f.download_url;
      }
    }
    return map;
  }, [post]);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setBody(post.body);
      setEventDate(post.event_date ?? "");
      setExternalUrl(post.external_url ?? "");
      // Текущие файлы поста → редактируемый набор (как PostFileIn).
      setFiles(
        post.files.map((f, i) => ({
          s3_key: f.s3_key,
          original_filename: f.original_filename,
          mime_type: f.mime_type,
          size_bytes: f.size_bytes,
          sort_order: i,
        }))
      );
      setFilesChanged(false);
    }
  }, [post]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!post) return;
    setError("");
    if (!title.trim()) {
      setError("Укажите заголовок — что за достижение");
      return;
    }
    setLoading(true);
    try {
      const payload: PostUpdateIn = {
        title: title.trim(),
        body: body.trim(),
        event_date: eventDate || null,
        external_url: externalUrl || null,
        external_url_kind: externalUrl ? detectUrlKind(externalUrl) : null,
      };
      // files передаём ТОЛЬКО если родитель их трогал — иначе бэкенд оставит
      // существующие как есть (избегаем лишних HEAD/replace на S3).
      if (filesChanged) {
        payload.files = files;
      }
      await updatePost(post.id, payload);
      await queryClient.invalidateQueries({ queryKey: ["post", post.id] });
      await queryClient.invalidateQueries({ queryKey: ["posts"] });
      navigate(`/cabinet/post/${post.id}`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Не удалось сохранить");
    } finally {
      setLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Загрузка…</p>
      </div>
    );
  }
  if (queryError || !post) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-2xl mx-auto px-6 py-8 text-center">
          <p className="text-[var(--color-accent)] mb-4">Публикация не найдена</p>
          <Link to="/cabinet" className="text-[var(--color-accent)] underline">
            ← В кабинет
          </Link>
        </main>
      </div>
    );
  }

  const canEdit =
    post.moderation_status === "pending" ||
    post.moderation_status === "needs_revision";

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-2xl mx-auto px-6 py-8 text-center">
          <p className="text-[var(--color-muted)] mb-4">
            Эту публикацию редактировать нельзя — она уже одобрена или отклонена.
          </p>
          <Link
            to={`/cabinet/post/${post.id}`}
            className="text-[var(--color-accent)] underline"
          >
            ← К публикации
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link
          to={`/cabinet/post/${post.id}`}
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] mb-2 inline-block"
        >
          ← К публикации
        </Link>
        <h1 className="text-3xl mb-2">Редактировать публикацию</h1>
        {post.moderation_status === "needs_revision" &&
          post.moderation_comment && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 px-3 py-2 mb-4 text-sm">
              <div className="font-bold mb-1">Учитель просит:</div>
              <div className="whitespace-pre-wrap">
                {post.moderation_comment}
              </div>
            </div>
          )}
        <p className="text-sm text-[var(--color-muted)] mb-6">
          После сохранения публикация снова уйдёт на проверку.
        </p>
        <ErrorBanner message={error} />

        <form
          onSubmit={onSubmit}
          className="bg-white p-6 border border-[var(--color-line)]"
        >
          <Input
            label="Заголовок"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
          />

          <Input
            label="Дата события"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            required
          />

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              Описание{" "}
              <span className="text-[var(--color-muted)] font-normal">
                — необязательно
              </span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              rows={4}
              className="w-full px-4 py-3 border-2 border-[var(--color-line)] bg-white focus:outline-none focus:border-[var(--color-accent-2)]"
            />
            <p className="mt-1 text-xs text-[var(--color-muted)] text-right">
              {body.length} / 2000
            </p>
          </div>

          <Input
            label="Видео-ссылка (Rutube / VK / YouTube — опц.)"
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://rutube.ru/video/..."
          />

          {/* Files — можно заменять. Важно для needs_revision: учитель часто
              просит «переснимите фото» — раньше для этого приходилось удалять
              весь пост и терять переписку с модератором. */}
          <FilesField
            files={files}
            onChange={setFiles}
            onTouched={() => setFilesChanged(true)}
            onError={setError}
            onUploadingChange={setUploading}
            existingPreviews={existingPreviews}
          />

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(`/cabinet/post/${post.id}`)}
            >
              Отмена
            </Button>
            <Button type="submit" variant="primary" loading={loading || uploading}>
              Сохранить и отправить
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
